import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedThankYou from '../animations/AnimatedThankYou';
import useCSRF from '../../hooks/useCSRF';
import { logger } from '../../utils/logger';
import { trackEvent } from '../../utils/analytics';

const FormContainer = styled.div`
  max-width: 600px;
  width: 100%;
  margin-top: 3rem;
`;

const WizardContainer = styled.div`
  position: relative;
  min-height: 400px;
  width: 100%;
`;

const CompletionContainer = styled(motion.div)`
  min-height: 400px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  pointer-events: auto;
`;

const StepTitle = styled.h4`
  font-family: 'Naveid Arabic DEMO', 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #000000;
  margin: 0 0 1rem 0;
`;

const StepDescription = styled.p`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
  margin: 0 0 2rem 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled(motion.input)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #000000;
  background: transparent;
  border: none;
  border-bottom: 2px solid #000000;
  padding: 0.75rem 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  
  &:focus {
    outline: none;
    border-bottom-color: #333;
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: #666;
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.4;
  }
`;

const TextArea = styled(motion.textarea)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #000000;
  background: transparent;
  border: none;
  border-bottom: 2px solid #000000;
  padding: 0.75rem 0;
  min-height: 100px;
  resize: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  
  &:focus {
    outline: none;
    border-bottom-color: #333;
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: #666;
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.4;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled(motion.button)`
  font-family: 'Naveid Arabic DEMO', 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #000000;
  background: transparent;
  border: 1px solid #000000;
  padding: 1rem 2rem;
  cursor: url('/icons/pointer.svg') 10 0, pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    background: #000000;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &:hover {
      background: transparent;
      color: #000000;
      transform: none;
      box-shadow: none;
    }
  }
`;

const BackButton = styled(Button)`
  background: transparent;
  border: 1px solid #ccc;
  color: #666;
  
  &:hover {
    background: #f5f5f5;
    color: #000000;
    border-color: #000000;
    transform: translateY(-2px) translateX(-2px);
    box-shadow: -4px 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0px) translateX(0px);
    box-shadow: -2px 2px 6px rgba(0, 0, 0, 0.05);
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  position: relative;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #000000 0%, #333333 100%);
  border-radius: 2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProgressText = styled.div`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #666;
  text-align: center;
  margin-top: 0.25rem;
  margin-bottom: 2.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ErrorMessage = styled.div`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #cc0000;
  margin-top: 0.5rem;
`;


const GeneralErrorMessage = styled.div`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #cc0000;
  background: #ffe6e6;
  border: 1px solid #ffcccc;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const ContactForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // CSRF token management
  const { makeAuthenticatedRequest, error: csrfError } = useCSRF();

  const steps = useMemo(() => ([
    {
      title: "What's your name?",
      description: "Let's start with the basics.",
      field: 'name',
      placeholder: 'Your full name',
      type: 'text'
    },
    {
      title: "What's your email?",
      description: "We'll use this to get back to you.",
      field: 'email',
      placeholder: 'your.email@example.com',
      type: 'email'
    },
    {
      title: "What's your phone number?",
      description: "In case we need to reach you directly.",
      field: 'phone',
      placeholder: '+61 4XX XXX XXX',
      type: 'tel'
    },
    {
      title: "Tell us about your project",
      description: "What can we help you with?",
      field: 'message',
      placeholder: 'Describe your project or how we can help...',
      type: 'textarea'
    }
  ]), []);
  const totalSteps = steps.length;

  const trackContactEvent = useCallback((action, params = {}) => {
    trackEvent(`contact_form_${action}`, {
      event_category: 'contact_form',
      step_index: currentStep + 1,
      total_steps: totalSteps,
      step_field: steps[currentStep]?.field,
      ...params
    });
  }, [currentStep, steps, totalSteps]);

  useEffect(() => {
    trackEvent('contact_form_view', {
      event_category: 'contact_form',
      total_steps: totalSteps
    });
  }, [totalSteps]);

  useEffect(() => {
    trackContactEvent('step_view');
  }, [currentStep, trackContactEvent]);

  const validateCurrentStep = () => {
    const currentField = steps[currentStep].field;
    const value = formData[currentField];
    const newErrors = { ...errors };

    if (!value.trim()) {
      newErrors[currentField] = 'This field is required';
    } else {
      // Field-specific validation
      switch (currentField) {
        case 'name':
          if (value.trim().length < 2) {
            newErrors[currentField] = 'Name must be at least 2 characters long';
          } else if (!/^[a-zA-Z\s\-']+$/.test(value)) {
            newErrors[currentField] = 'Name can only contain letters, spaces, hyphens, and apostrophes';
          } else if (value.trim().length > 100) {
            newErrors[currentField] = 'Name must be less than 100 characters';
          } else {
            delete newErrors[currentField];
          }
          break;
          
        case 'email':
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            newErrors[currentField] = 'Please enter a valid email address';
          } else if (value.length > 255) {
            newErrors[currentField] = 'Email must be less than 255 characters';
          } else {
            delete newErrors[currentField];
          }
          break;
          
        case 'phone':
          const phoneDigits = value.replace(/[^0-9+]/g, '');
          if (!/^(\+61|0)?4[0-9]{8}$/.test(phoneDigits)) {
            newErrors[currentField] = 'Please enter a valid Australian phone number (e.g., +61 4XX XXX XXX)';
          } else if (value.length > 20) {
            newErrors[currentField] = 'Phone number must be less than 20 characters';
          } else {
            delete newErrors[currentField];
          }
          break;
          
        case 'message':
          if (value.trim().length < 10) {
            newErrors[currentField] = 'Message must be at least 10 characters long';
          } else if (value.length > 2000) {
            newErrors[currentField] = 'Message must be less than 2000 characters';
          } else {
            delete newErrors[currentField];
          }
          break;
          
        default:
          delete newErrors[currentField];
      }
    }

    setErrors(newErrors);

    if (newErrors[currentField]) {
      trackContactEvent('validation_error', {
        field: currentField,
        message: newErrors[currentField]
      });
    }

    return !newErrors[currentField];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      trackContactEvent('step_advance', { direction: 'forward' });
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    trackContactEvent('step_advance', { direction: 'backward' });
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      return;
    }
    
    // Prevent double submissions
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    trackContactEvent('submission_attempt');
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      // Use the authenticated request helper which automatically includes CSRF token
      const response = await makeAuthenticatedRequest(`${apiBaseUrl}/api/inquiry.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      // Check content type before parsing
      const contentType = response.headers.get('content-type') || '';
      let result;
      
      if (contentType.includes('application/json')) {
        result = await response.json();
      } else {
        // Handle non-JSON responses (e.g., WAF/bot protection responses)
        const text = await response.text();
        console.warn('Non-JSON response received:', text.substring(0, 200));
        
        // Check for 409 Conflict (often from WAF/bot protection)
        if (response.status === 409) {
          trackContactEvent('submission_blocked', { reason: 'conflict' });
          setErrors({ 
            general: 'Request blocked by security service. Please refresh the page and try again, or contact support if the issue persists.' 
          });
          return;
        }
        
        throw new Error('Server returned an unexpected response format. Please try again.');
      }
      
      if (!response.ok) {
        // Handle CSRF token errors specifically
        if (response.status === 403 && result.errors && result.errors.csrf) {
          trackContactEvent('submission_blocked', { reason: 'csrf' });
          setErrors({ 
            general: 'Security token expired. Please try again.' 
          });
          return;
        }
        
        // Handle rate limiting error specifically
        if (response.status === 429) {
          trackContactEvent('submission_blocked', { reason: 'rate_limited' });
          setErrors({ 
            general: 'Too many requests. Please wait a moment before trying again.' 
          });
          return;
        }
        
        // Handle 409 Conflict errors (duplicate submissions or WAF blocks)
        if (response.status === 409) {
          trackContactEvent('submission_blocked', { reason: 'conflict' });
          const errorMessage = result.message || 
            (result.errors && result.errors.duplicate 
              ? 'This submission appears to be a duplicate. If you believe this is an error, please contact support.'
              : 'This submission may have already been processed, or was blocked by security. Please refresh the page and try again.');
          setErrors({ 
            general: errorMessage
          });
          return;
        }
        
        // Handle validation errors from server
        if (result.errors && typeof result.errors === 'object') {
          trackContactEvent('submission_validation_error', {
            fields: Object.keys(result.errors)
          });
          setErrors(result.errors);
          // Go back to the first step with an error
          const firstErrorField = Object.keys(result.errors)[0];
          const errorStepIndex = steps.findIndex(step => step.field === firstErrorField);
          if (errorStepIndex !== -1) {
            setCurrentStep(errorStepIndex);
            trackContactEvent('step_jump_to_error', {
              error_field: firstErrorField
            });
          }
          return;
        }
        throw new Error(result.message || 'Form submission failed');
      }
      
      if (result.success) {
        logger.info('Form submitted successfully:', result);
        trackContactEvent('submission_success');
        setIsSubmitted(true);
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      trackContactEvent('submission_failure', {
        reason: error.message || 'unknown'
      });
      // Set a general error message
      setErrors({ 
        general: error.message.includes('fetch') 
          ? 'Network error. Please check your connection and try again.' 
          : error.message || 'Failed to submit form. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && currentStep < steps.length - 1) {
      handleNext();
    } else if (e.key === 'Enter' && currentStep === steps.length - 1) {
      handleSubmit();
    }
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <FormContainer>
      <WizardContainer>
        {!isSubmitted && (
          <>
            <ProgressBarContainer>
              <ProgressBar
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </ProgressBarContainer>
            <ProgressText>
              Step {currentStep + 1} of {steps.length}
            </ProgressText>
          </>
        )}
        
        <AnimatePresence mode="wait">
          {isSubmitted ? (
            <CompletionContainer
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
                scale: { duration: 0.3 }
              }}
            >
              <AnimatedThankYou
                mainMessage="Thank You!"
                subMessage="We'll get back to you soon."
                showCheckmark={true}
              />
            </CompletionContainer>
          ) : (
            <StepContainer
              key={currentStep}
              initial={{ opacity: 0, x: 100, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -100, scale: 0.95 }}
              transition={{ 
                duration: 0.4, 
                ease: [0.4, 0, 0.2, 1],
                scale: { duration: 0.3 }
              }}
            >
              <StepTitle>{currentStepData.title}</StepTitle>
              <StepDescription>{currentStepData.description}</StepDescription>
              
              {errors.general && (
                <GeneralErrorMessage>{errors.general}</GeneralErrorMessage>
              )}
              
              {csrfError && (
                <GeneralErrorMessage>
                  Security error: {csrfError}. Please refresh the page and try again.
                </GeneralErrorMessage>
              )}
              
              <FormGroup>
                <Label htmlFor={currentStepData.field}>
                  {currentStepData.field.charAt(0).toUpperCase() + currentStepData.field.slice(1)}
                </Label>
                {currentStepData.type === 'textarea' ? (
                  <TextArea
                    id={currentStepData.field}
                    name={currentStepData.field}
                    value={formData[currentStepData.field]}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={currentStepData.placeholder}
                    tabIndex={0}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  />
                ) : (
                  <Input
                    type={currentStepData.type}
                    id={currentStepData.field}
                    name={currentStepData.field}
                    value={formData[currentStepData.field]}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={currentStepData.placeholder}
                    tabIndex={0}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  />
                )}
                {errors[currentStepData.field] && (
                  <ErrorMessage>{errors[currentStepData.field]}</ErrorMessage>
                )}
              </FormGroup>
              
              <ButtonContainer>
                {currentStep > 0 && (
                  <BackButton 
                    onClick={handleBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    ← Back
                  </BackButton>
                )}
                <Button 
                  onClick={isLastStep ? handleSubmit : handleNext}
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {isSubmitting ? (
                    <motion.span
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      Sending...
                    </motion.span>
                  ) : isLastStep ? (
                    'Send Message →'
                  ) : (
                    'Next →'
                  )}
                </Button>
              </ButtonContainer>
            </StepContainer>
          )}
        </AnimatePresence>
      </WizardContainer>
    </FormContainer>
  );
};

export default ContactForm;
